<template>
    <div class="v-filter">
        <template v-if="checkRequestFilterSettings()">
            <div v-show="!showSettingsFilter">
                <div class="v-filter__head">
                    <span class="v-filter__title">Фильтры</span>

                    <button
                        class="v-filter__icon-btn"
                        type="button"
                        title="Настроить набор полей"
                        @click="showSettingsFilter = true"
                    >
                        <CogIcon :size="18"></CogIcon>
                    </button>
                </div>

                <div class="v-filter__grid">
                    <div class="v-filter__field" :key="filter.name" v-for="filter in activeFilters">
                        <span class="v-filter__label">{{ filter.title }}</span>

                        <component :is="getFilterName(filter.name)" :ref="filter.name"></component>
                    </div>
                </div>

                <div class="v-filter__foot">
                    <span class="v-filter__hint"
                        >Набор полей настраивается через шестерёнку и сохраняется в профиле</span
                    >

                    <button class="v-filter__btn v-filter__btn_ghost" type="button" @click.prevent="onReset">
                        Сбросить
                    </button>
                    <button class="v-filter__btn v-filter__btn_green" type="button" @click.prevent="onApply">
                        Применить
                    </button>
                </div>
            </div>

            <div v-show="showSettingsFilter">
                <div class="v-filter__head">
                    <button
                        class="v-filter__icon-btn"
                        type="button"
                        title="Назад к фильтрам"
                        @click="showSettingsFilter = false"
                    >
                        <ArrowLeftIcon :size="18"></ArrowLeftIcon>
                    </button>

                    <span class="v-filter__title">Набор полей</span>
                </div>

                <div class="v-filter__set-grid">
                    <div class="v-filter__set-col" :key="i" v-for="(group, i) in filterGroups">
                        <div class="v-filter__set-title">{{ groupTitles[i] }}</div>

                        <ul class="v-filter__set-items">
                            <li class="v-filter__set-item" :key="filter.name" v-for="filter in group">
                                <InputCheckbox :name="filter.name" @input="onCheckboxChange" v-model="filter.active">{{
                                    filter.title
                                }}</InputCheckbox>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="v-filter__foot">
                    <span class="v-filter__hint">Изменения сохраняются автоматически</span>

                    <button
                        class="v-filter__btn v-filter__btn_green"
                        type="button"
                        @click.prevent="showSettingsFilter = false"
                    >
                        Готово
                    </button>
                </div>
            </div>
        </template>

        <template v-else>
            <div class="v-filter__loading">
                <VSpinner></VSpinner>
            </div>
        </template>
    </div>
</template>
<script>
import { namespace } from 'vuex-class';
import { Vue, Component, Prop } from 'vue-property-decorator';

import VSpinner from '@/views/components/VProgress/VSpinner';
import InputCheckbox from '@/views/components/VCheckbox/InputCheckbox';

import Filters from './Filters';
import upperFirst from 'lodash/upperFirst';
import { isNumber } from 'validate.js';
import { buildFilterChip } from '@/js/filterChips';
import { mergePanelStatuses } from '@/js/requestFilters';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import ArrowLeftIcon from 'vue-material-design-icons/ArrowLeft.vue';

const Account = namespace('account');

@Component({
    components: {
        VSpinner,
        InputCheckbox,
        CogIcon,
        ArrowLeftIcon,
        ...Filters,
    },
})
class VFilter extends Vue {
    @Prop({ type: Object, required: true }) filters;

    @Account.Action CREATE_REQUEST_FILTER_SETTINGS;
    @Account.Action UPDATE_REQUEST_FILTER_SETTINGS;

    filterGroups = [];
    showSettingsFilter = false;
    filterSettingUpdate = false;
    savedFilters = {};
    groupTitles = ['Период', 'Классификация', 'Участники'];

    get allFilters() {
        return this.filterGroups.reduce((carry, group) => {
            carry.push(...group);
            return carry;
        }, []);
    }

    get activeFilters() {
        return this.allFilters.filter((filter) => filter.active);
    }

    onApply() {
        const values = this.getValueByActiveFilters();

        // Статусные поля панели редактируют state.statuses, а не панельный payload.
        // Их значения берём отдельно от values: getValueByActiveFilters() выбрасывает
        // пустые значения, и снятые в мультиселекте статусы до слияния бы не дошли —
        // mergePanelStatuses принял бы поле за отключённое и вернул прежний выбор.
        const statuses = mergePanelStatuses(this.filters.state.statuses, this.getStatusFieldValues());

        delete values.filterStatus;
        delete values.filterExpired;

        const chips = this.activeFilters
            .filter((filter) => values[filter.name] !== undefined)
            .map((filter) => buildFilterChip(filter.name, filter.title, this.getFilterByName(filter.name)));

        this.filters.applyPanel(values, chips);
        this.filters.setStatusesFromPanel(statuses);

        this.$emit('applied');
    }

    onReset() {
        this.resetActiveFilters();
        this.filters.resetPanel();
        this.$emit('applied');
    }

    syncFromState() {
        const { panel, statuses } = this.filters.state;

        this.activeFilters.forEach((filter) => {
            const ref = this.getFilterByName(filter.name);
            if (!ref) return;

            if (filter.name === 'filterStatus') {
                const numeric = (statuses || []).filter((s) => s !== 'new' && s !== 'overdue');
                if (typeof ref.setValue === 'function') ref.setValue(numeric);
            } else if (filter.name === 'filterExpired') {
                if (typeof ref.setValue === 'function') ref.setValue((statuses || []).includes('overdue') ? 2 : 1);
            } else if (panel[filter.name] !== undefined) {
                if (typeof ref.setValue === 'function') ref.setValue(panel[filter.name]);
            } else {
                ref.reset();
            }
        });
    }

    onCheckboxChange() {
        if (this.filterSettingUpdate) return;

        this.filterSettingUpdate = true;

        setTimeout(() => {
            this.filterSettingUpdate = false;
            this.updateRequestFilterSettings();
        }, 5000);
    }

    getValueByActiveFilters() {
        return this.activeFilters.reduce((carry, filter) => {
            const ref = this.getFilterByName(filter.name);
            if (!ref) return carry;

            const value = ref.getValue();

            if ((value && value.length) || isNumber(value) || value === 0) {
                carry[filter.name] = value;
            }

            return carry;
        }, {});
    }

    /**
     * Значения статусных полей для mergePanelStatuses — в отличие от панельного payload,
     * здесь важно именно присутствие поля: отсутствует — панель им не управляет и трогать
     * статусы не вправе, присутствует с пустым значением — пользователь выбор снял.
     */
    getStatusFieldValues() {
        return this.activeFilters.reduce((carry, filter) => {
            if (filter.name !== 'filterStatus' && filter.name !== 'filterExpired') {
                return carry;
            }

            const ref = this.getFilterByName(filter.name);
            if (!ref) return carry;

            const value = ref.getValue();

            if (filter.name === 'filterStatus') {
                carry.filterStatus = value || [];
            } else {
                // Очищенное поле просрочки равнозначно варианту «Все»: пользователь видит
                // плейсхолдер, значит никакого ограничения по просрочке он не ставил.
                carry.filterExpired = value === undefined || value === null ? 1 : value;
            }

            return carry;
        }, {});
    }

    getFilterName(name) {
        return upperFirst(name);
    }

    getFilterByName(name) {
        const refs = this.$refs[name];

        return refs && refs[0];
    }

    getFilterSettings() {
        return this.allFilters.reduce((carry, filter) => {
            carry[filter.name] = filter.active;
            return carry;
        }, {});
    }

    resetActiveFilters() {
        this.activeFilters.forEach((filter) => {
            const ref = this.getFilterByName(filter.name);
            if (ref) ref.reset();
        });
    }

    loadDefaultFilterSettings() {
        this.filterGroups = Object.values(this.$config.filterGroups);
    }

    loadAccountFilterSettings() {
        this.filterGroups = Object.values(this.$config.filterGroups).map((filters) =>
            filters.map((filter) => {
                filter.active = !!this.$_account.requestFilterSettings[filter.name];

                return filter;
            }),
        );
    }

    checkRequestFilterSettings() {
        return !!this.$_account.requestFilterSettings;
    }

    createRequestFilterSettings() {
        this.CREATE_REQUEST_FILTER_SETTINGS(this.getFilterSettings());
    }

    updateRequestFilterSettings() {
        this.UPDATE_REQUEST_FILTER_SETTINGS({
            requestFilterSettings: this.getFilterSettings(),
            requestFilterSettingsId: this.$_account.requestFilterSettings.id,
        });
    }

    created() {
        if (!this.checkRequestFilterSettings()) {
            this.loadDefaultFilterSettings();
            this.createRequestFilterSettings();
        } else {
            this.loadAccountFilterSettings();
        }
        this.$bus.on('tools:clear', this.resetActiveFilters);
    }

    beforeDestroy() {
        this.$bus.off('tools:clear', this.resetActiveFilters);
    }
}

export default VFilter;
</script>
<style lang="scss">
.v-filter {
    display: flex;
    width: 100%;
    flex-direction: column;
    background: var(--color-white);
    border: 1px solid #e3e5e8;
    border-radius: 14px;
    box-shadow: 0 18px 44px rgba(44, 44, 44, 0.16);
    padding: 18px 20px;

    &__loading {
        display: flex;
        width: 100%;
        height: 120px;
        align-items: center;
        justify-content: center;
    }

    &__head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
    }

    &__title {
        font: 600 0.9375rem var(--font-family);
        color: #3c4043;
        margin-right: auto;
    }

    &__icon-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #e3e5e8;
        background: var(--color-white);
        border-radius: 8px;
        color: #5f6368;
        cursor: pointer;

        &:hover {
            background: #f6f7f8;
        }
    }

    &__grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px 18px;
    }

    &__label {
        display: block;
        font: 600 0.6875rem var(--font-family);
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #9aa0a6;
        margin-bottom: 6px;
    }

    &__foot {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 18px;
    }

    &__hint {
        font-size: 0.75rem;
        color: #9aa0a6;
        margin-right: auto;
    }

    &__btn {
        height: 38px;
        border: 0;
        border-radius: 9px;
        font: 500 0.84375rem var(--font-family);
        padding: 0 22px;
        cursor: pointer;
    }

    &__btn_green {
        background: $green;
        color: var(--text-white);

        &:hover {
            background: #15754a;
        }
    }

    &__btn_ghost {
        background: transparent;
        color: #d9641e;
        border: 1px solid #f5d5c0;

        &:hover {
            background: #fdf0e8;
        }
    }

    &__set-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 8px 22px;
    }

    &__set-title {
        font: 600 0.6875rem var(--font-family);
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #9aa0a6;
        margin: 2px 0 8px;
    }

    &__set-items {
        margin: 0;
        padding: 0;
        list-style-type: none;
    }

    &__set-item {
        padding: 4px 0;
    }
}

@media (max-width: $mobile-breakpoint) {
    .v-filter {
        padding: 14px;

        &__grid,
        &__set-grid {
            grid-template-columns: 1fr;
        }

        &__hint {
            display: none;
        }
    }
}
</style>
